/**
 * Platform-Agnostic Analytics API Client
 *
 * Handles analytics operations using the PERS backend.
 * Uses @explorins/pers-shared DTOs for consistency with backend.
 */
class AnalyticsApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Get transaction analytics with filtering and aggregation
     */
    async getTransactionAnalytics(request) {
        return this.apiClient.post('/analytics/transactions', request);
    }
    /**
     * ADMIN: Get campaign claim analytics with aggregation (charts, metrics, grouping)
     *
     * This endpoint is for aggregated analytics only (groupBy/metrics).
     * For enriched list data with nested objects, use campaign.getClaims() instead.
     *
     * @example Claims per campaign
     * ```typescript
     * const response = await analyticsApi.getCampaignClaimAnalytics({
     *   filters: { status: 'COMPLETED' },
     *   groupBy: ['campaignId'],
     *   metrics: ['count'],
     *   sortBy: 'count',
     *   sortOrder: SortOrder.DESC,
     *   limit: 10
     * });
     * ```
     *
     * @example Claims over time by status
     * ```typescript
     * const response = await analyticsApi.getCampaignClaimAnalytics({
     *   groupBy: ['month', 'status'],
     *   metrics: ['count'],
     *   sortBy: 'month',
     *   sortOrder: SortOrder.DESC,
     *   startDate: new Date('2026-01-01'),
     *   endDate: new Date('2026-12-31')
     * });
     * ```
     */
    async getCampaignClaimAnalytics(request) {
        return this.apiClient.post('/analytics/campaign-claims', request);
    }
    /**
     * ADMIN: Get redemption redeem analytics with aggregation (charts, metrics, grouping)
     *
     * This endpoint is for aggregated analytics only (groupBy/metrics).
     * For enriched list data with nested objects, use redemption.getRedeems() instead.
     *
     * @example Redeems per redemption
     * ```typescript
     * const response = await analyticsApi.getRedemptionRedeemAnalytics({
     *   groupBy: ['redemptionId'],
     *   metrics: ['count'],
     *   sortBy: 'count',
     *   sortOrder: SortOrder.DESC,
     *   limit: 10
     * });
     * ```
     *
     * @example Redeems by status over time
     * ```typescript
     * const response = await analyticsApi.getRedemptionRedeemAnalytics({
     *   groupBy: ['month', 'status'],
     *   metrics: ['count'],
     *   sortBy: 'month',
     *   sortOrder: SortOrder.DESC,
     *   startDate: new Date('2026-01-01'),
     *   endDate: new Date('2026-12-31')
     * });
     * ```
     *
     * @example Redeems by business
     * ```typescript
     * const response = await analyticsApi.getRedemptionRedeemAnalytics({
     *   filters: { status: 'COMPLETED' },
     *   groupBy: ['businessId'],
     *   metrics: ['count'],
     *   sortBy: 'count',
     *   sortOrder: SortOrder.DESC
     * });
     * ```
     */
    async getRedemptionRedeemAnalytics(request) {
        return this.apiClient.post('/analytics/redemption-redeems', request);
    }
    /**
     * ADMIN: Get user analytics with engagement metrics
     *
     * Returns aggregated user statistics including engagement rate, active users,
     * transaction metrics, and per-active-user averages (more accurate than all-user averages).
     *
     * Request structure matches TransactionAnalytics and CampaignClaimAnalytics for consistency:
     * - filters object for business-specific scoping
     * - startDate/endDate at root level for date range filtering
     *
     * @param request - Analytics request with optional filters and date range
     * @returns Aggregated user metrics with per-user and per-active-user averages
     *
     * @example Basic user analytics (all time, all businesses)
     * ```typescript
     * const analytics = await analyticsApi.getUserAnalytics({});
     * console.log(`Total users: ${analytics.totalUsers}`);
     * console.log(`Active users: ${analytics.activeUsers}`);
     * console.log(`Engagement rate: ${analytics.engagementRate}%`);
     *
     * // Per-user averages (includes inactive users)
     * console.log(`Avg transactions per user: ${analytics.averageTransactionsPerUser}`);
     *
     * // Per-active-user averages (only engaged users - more useful!)
     * console.log(`Avg transactions per active user: ${analytics.averageTransactionsPerActiveUser}`);
     * ```
     *
     * @example Filtered by date range
     * ```typescript
     * const analytics = await analyticsApi.getUserAnalytics({
     *   startDate: new Date('2026-01-01'),
     *   endDate: new Date('2026-01-31')
     * });
     * console.log(`January metrics:`);
     * console.log(`Active users: ${analytics.activeUsers}`);
     * console.log(`New users: ${analytics.newUsers}`);
     * console.log(`Date range applied: ${analytics.metadata.dateRange?.startDate} - ${analytics.metadata.dateRange?.endDate}`);
     * ```
     *
     * @example Business-specific analytics with filters
     * ```typescript
     * const analytics = await analyticsApi.getUserAnalytics({
     *   filters: { businessId: 'biz-123' },
     *   startDate: new Date('2026-01-01'),
     *   endDate: new Date('2026-12-31')
     * });
     * console.log(`Business customer engagement in 2026:`);
     * console.log(`Active customers: ${analytics.activeUsers}`);
     * console.log(`Avg claims per active customer: ${analytics.averageClaimsPerActiveUser.toFixed(2)}`);
     * console.log(`Customer engagement rate: ${analytics.engagementRate.toFixed(1)}%`);
     * ```
     */
    async getUserAnalytics(request = {}) {
        return this.apiClient.post('/analytics/users', request);
    }
    /**
     * ADMIN: Get user transaction ranking with enriched user data
     *
     * Returns ranked list of users with full user details and transaction metrics.
     * Data enrichment happens via efficient SQL JOINs + UNION (handles legacy transactions).
     *
     * Use Cases:
     * - Admin leaderboards showing top users by activity
     * - User engagement analysis with full user context
     * - Identifying power users for campaigns
     *
     * @param request - Ranking request with filters, sorting, and limit
     * @returns Ranked list with user details (email, externalUserId) and transaction metrics
     *
     * @example Top 50 users by transaction count
     * ```typescript
     * const ranking = await analyticsApi.getUserRanking({
     *   sortBy: 'totalTransactions',
     *   sortOrder: SortOrder.DESC,
     *   limit: 50
     * });
     *
     * ranking.results.forEach((user, index) => {
     *   console.log(`#${index + 1}: ${user.email || user.externalUserId} - ${user.totalTransactions} transactions`);
     * });
     * ```
     *
     * @example Top users by STAMP spending
     * ```typescript
     * const ranking = await analyticsApi.getUserRanking({
     *   filters: { tokenType: 'STAMP' },
     *   sortBy: 'tokenSpent',
     *   sortOrder: SortOrder.DESC,
     *   limit: 20
     * });
     * ```
     *
     * @example Business-specific leaderboard with date range
     * ```typescript
     * const ranking = await analyticsApi.getUserRanking({
     *   filters: {
     *     businessId: 'business-uuid-here',
     *     tokenType: 'CREDIT'
     *   },
     *   sortBy: 'totalTransactions',
     *   sortOrder: SortOrder.DESC,
     *   limit: 100,
     *   startDate: new Date('2026-01-01'),
     *   endDate: new Date('2026-12-31')
     * });
     * console.log(`Top ${ranking.totalUsers} users for business in 2026`);
     * ```
     */
    async getUserRanking(request = {}) {
        return this.apiClient.post('/analytics/users/ranking', request);
    }
    /**
     * ADMIN: Get business transaction ranking with enriched business data
     *
     * Returns ranked list of businesses with full business details and transaction metrics.
     * Data enrichment happens via efficient SQL JOINs + UNION (handles legacy transactions).
     *
     * @param request - Ranking request with filters, sorting, and limit
     * @returns Ranked list with business details and transaction metrics
     *
     * @example Top 50 businesses by transaction count
     * ```typescript
     * const ranking = await analyticsApi.getBusinessRanking({
     *   sortBy: 'totalTransactions',
     *   sortOrder: SortOrder.DESC,
     *   limit: 50
     * });
     * ```
     */
    async getBusinessRanking(request = {}) {
        return this.apiClient.post('/analytics/businesses/ranking', request);
    }
    /**
     * ADMIN: Get monthly user retention analytics
     *
     * Returns monthly retention data with active/new/returning users and retention rates.
     * Replaces 13 separate API calls with 1 efficient recursive CTE query.
     *
     * @param request - Retention analytics request with monthsBack and filters
     * @returns Monthly retention data with user metrics and retention rates
     *
     * @example Last 13 months retention
     * ```typescript
     * const retention = await analyticsApi.getRetentionAnalytics({
     *   monthsBack: 13
     * });
     * ```
     */
    async getRetentionAnalytics(request = {}) {
        return this.apiClient.post('/analytics/users/retention', request);
    }
    // ==========================================
    // TAG ANALYTICS
    // ==========================================
    /**
     * ADMIN: Get tag usage analytics across entities
     *
     * Aggregates tag usage across all taggable entities (campaigns, redemptions,
     * businesses, token metadata, user status types). Perfect for:
     * - Tag autocomplete/suggestions
     * - Tag management dashboards
     * - Usage analytics
     *
     * @param request - Optional filters for entity types
     * @returns Aggregated tag usage with per-entity-type breakdown
     *
     * @example Get all tags across all entities
     * ```typescript
     * const allTags = await analyticsApi.getTagAnalytics();
     * console.log(`${allTags.totalTags} unique tags, ${allTags.totalUsage} total usages`);
     *
     * // Use for autocomplete suggestions
     * const suggestions = allTags.tags.map(t => t.tag);
     * ```
     *
     * @example Get tags only from campaigns and businesses
     * ```typescript
     * const filteredTags = await analyticsApi.getTagAnalytics({
     *   entityTypes: [TaggableEntityType.CAMPAIGN, TaggableEntityType.BUSINESS]
     * });
     * ```
     *
     * @example Tag usage breakdown
     * ```typescript
     * const analytics = await analyticsApi.getTagAnalytics();
     * analytics.tags.forEach(({ tag, count, usageByEntityType }) => {
     *   console.log(`${tag}: ${count} total`);
     *   usageByEntityType.forEach(({ entityType, count }) => {
     *     console.log(`  - ${entityType}: ${count}`);
     *   });
     * });
     * ```
     */
    async getTagAnalytics(request = {}) {
        const params = new URLSearchParams();
        if (request.entityTypes?.length) {
            params.set('entityTypes', request.entityTypes.join(','));
        }
        const queryString = params.toString();
        const url = queryString ? `/analytics/tags?${queryString}` : '/analytics/tags';
        return this.apiClient.get(url);
    }
}

/**
 * Platform-Agnostic Analytics Service
 *
 * Contains analytics business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 *
 * Focuses only on actual backend capabilities.
 */
class AnalyticsService {
    constructor(analyticsApi) {
        this.analyticsApi = analyticsApi;
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Get transaction analytics with filtering and aggregation
     */
    async getTransactionAnalytics(request) {
        return this.analyticsApi.getTransactionAnalytics(request);
    }
    /**
     * ADMIN: Get campaign claim analytics with aggregation
     */
    async getCampaignClaimAnalytics(request) {
        return this.analyticsApi.getCampaignClaimAnalytics(request);
    }
    /**
     * ADMIN: Get redemption redeem analytics with aggregation
     *
     * Returns aggregated redeem data with flexible grouping and metrics.
     * Perfect for charts and dashboard summaries.
     */
    async getRedemptionRedeemAnalytics(request) {
        return this.analyticsApi.getRedemptionRedeemAnalytics(request);
    }
    /**
     * ADMIN: Get user analytics with engagement metrics
     *
     * Returns aggregated user statistics including engagement rate, active users,
     * transaction metrics, and claims/redemption averages.
     */
    async getUserAnalytics(request = {}) {
        return this.analyticsApi.getUserAnalytics(request);
    }
    /**
     * ADMIN: Get user transaction ranking with enriched user data
     *
     * Returns ranked list of users with full user details (email, externalUserId)
     * and transaction metrics. Efficient data enrichment via SQL JOINs + UNION.
     */
    async getUserRanking(request = {}) {
        return this.analyticsApi.getUserRanking(request);
    }
    /**
     * ADMIN: Get business transaction ranking with enriched business data
     *
     * Returns ranked list of businesses with full business details and transaction metrics.
     */
    async getBusinessRanking(request = {}) {
        return this.analyticsApi.getBusinessRanking(request);
    }
    /**
     * ADMIN: Get monthly user retention analytics
     *
     * Returns monthly retention data with user metrics and retention rates.
     * Replaces 13 separate API calls with 1 efficient query.
     */
    async getRetentionAnalytics(request = {}) {
        return this.analyticsApi.getRetentionAnalytics(request);
    }
    // ==========================================
    // TAG ANALYTICS
    // ==========================================
    /**
     * ADMIN: Get tag usage analytics across entities
     *
     * Aggregates tag usage across all taggable entities for autocomplete,
     * tag management dashboards, and usage analytics.
     */
    async getTagAnalytics(request = {}) {
        return this.analyticsApi.getTagAnalytics(request);
    }
}

export { AnalyticsApi as A, AnalyticsService as a };
//# sourceMappingURL=analytics-service-CF7hSwhy.js.map
